import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { ChevronDown, ChevronRight, FileText, Folder } from "react-feather";
import { spacing } from "../../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../../design-system/tokens/typography";
import { rawColors } from "../../../../design-system/tokens/colors";
import { borderRadius } from "../../../../design-system/tokens/borders";
import {
  DataTable,
  ScrollableTableViewport,
  type DataTableColumn,
} from "../../../molecules";
import { Spinner } from "../../../atoms";
import {
  connectorPreviewCountsApi,
  type ConnectorPreviewCountsEntryPayload,
} from "../../../../services/api";
import type { StepRenderProps } from "../ConnectorWizardModal.types";
import type { BiDatasetTreeNode, TableMeta } from "./selectTreeMapper";

/** Mirrors SelectStep's STEP_MIN_HEIGHT so the modal doesn't shrink when
 *  navigating from Select → Preview (the table list here is much shorter
 *  than the Select content). */
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  flex: 0 0 auto;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  padding: 0 ${spacing[1]};
`;

/** Grows with row count until the modal body limit, then scrolls. */
const ListArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const CountsErrorNote = styled.p`
  margin: 0;
  padding: 0 ${spacing[1]};
  ${typographyMixin("body", 4)}
  color: ${rawColors.accent.red500};
`;

const EmptyMsg = styled.p`
  margin: 0;
  padding: ${spacing[6]};
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.mediumEmphasis};
  text-align: center;
`;

const CountsSpinner = styled(Spinner)`
  color: ${rawColors.text.lowEmphasis};
`;

const CenteredInfoMessage = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${spacing[4]};
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

const TreePreviewWrap = styled.div`
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[2]} 0;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TreeRow = styled.div<{ $depth: number; $isLast: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 14px 14px 1fr;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[1]} ${spacing[3]};
  padding-left: ${({ $depth }) => `${12 + Math.min($depth, 6) * 20}px`};
  color: ${rawColors.text.highEmphasis};
  ${typographyMixin("body", 3)}

  ${({ $depth, $isLast }) =>
    $depth > 0
      ? `
    &::before {
      content: "";
      position: absolute;
      left: ${12 + ($depth - 1) * 20 + 6}px;
      top: 0;
      bottom: ${$isLast ? "50%" : "0"};
      width: 1px;
      background: ${rawColors.stroke[300]};
    }

    &::after {
      content: "";
      position: absolute;
      left: ${12 + ($depth - 1) * 20 + 6}px;
      top: 50%;
      width: 14px;
      height: 1px;
      background: ${rawColors.stroke[300]};
    }
  `
      : ""}
`;

const TreeExpander = styled.button`
  width: 14px;
  height: 14px;
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;
`;

const TreeLabel = styled.span`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface PreviewRow {
  key: string;
  schema: string;
  table: string;
}

const parseSelectedTable = (raw: unknown): PreviewRow | null => {
  if (typeof raw !== "string") return null;
  const dotIdx = raw.indexOf(".");
  if (dotIdx <= 0) return null;
  return {
    key: raw,
    schema: raw.slice(0, dotIdx),
    table: raw.slice(dotIdx + 1),
  };
};

const filterSelectedBiTree = (
  nodes: BiDatasetTreeNode[],
  selectedLeafKeys: Set<string>,
): BiDatasetTreeNode[] => {
  const visit = (node: BiDatasetTreeNode): BiDatasetTreeNode | null => {
    if (node.children.length === 0) {
      return selectedLeafKeys.has(node.key) ? node : null;
    }
    const keptChildren = node.children
      .map(visit)
      .filter((child): child is BiDatasetTreeNode => child !== null);
    if (keptChildren.length === 0) return null;
    return { ...node, children: keptChildren };
  };
  return nodes
    .map(visit)
    .filter((node): node is BiDatasetTreeNode => node !== null);
};

const collectExpandableNodeIds = (nodes: BiDatasetTreeNode[]): Set<string> => {
  const expanded = new Set<string>();
  const visit = (node: BiDatasetTreeNode) => {
    if (node.children.length > 0) {
      expanded.add(node.id);
      node.children.forEach(visit);
    }
  };
  nodes.forEach(visit);
  return expanded;
};

/**
 * PreviewStep — review of the tables the user picked before import.
 *
 * Row metadata (rowCount/columnCount) is sourced from the wizard's
 * `tablesByKey` cache when available; otherwise falls back to em-dashes.
 */
export const PreviewStep: React.FC<StepRenderProps> = ({
  step,
  values,
  wizardConfig,
  catalogKey,
}) => {
  const isBiVariant = wizardConfig.behavior?.uiVariant === "bi_relational_tree";
  const importMode = values.importMode;
  const previewHeading =
    typeof step.title === "string" && step.title.trim().length > 0
      ? step.title.trim()
      : "Selected tables";
  const previewAllSelectedMessage = useMemo(() => {
    const treeHints = wizardConfig.behavior?.treeHints;
    if (
      treeHints &&
      typeof treeHints === "object" &&
      typeof treeHints.preview_all_selected_message === "string" &&
      treeHints.preview_all_selected_message.trim().length > 0
    ) {
      return treeHints.preview_all_selected_message.trim();
    }
    if (typeof step.note === "string" && step.note.trim().length > 0) {
      return step.note.trim();
    }
    return "All catalog items selected.";
  }, [step.note, wizardConfig.behavior?.treeHints]);
  const rows = useMemo<PreviewRow[]>(() => {
    const raw = Array.isArray(values.selectedTables)
      ? values.selectedTables
      : [];
    return raw
      .map(parseSelectedTable)
      .filter((r): r is PreviewRow => r !== null);
  }, [values.selectedTables]);
  const biTreeNodes = useMemo<BiDatasetTreeNode[]>(() => {
    const raw = values.biTreeNodes;
    return Array.isArray(raw) ? (raw as BiDatasetTreeNode[]) : [];
  }, [values.biTreeNodes]);
  const selectedLeafKeys = useMemo(
    () => new Set(rows.map((row) => row.key)),
    [rows],
  );
  const selectedBiTree = useMemo(
    () => filterSelectedBiTree(biTreeNodes, selectedLeafKeys),
    [biTreeNodes, selectedLeafKeys],
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedIds(collectExpandableNodeIds(selectedBiTree));
  }, [selectedBiTree]);

  const groupedBySchema = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const row of rows) {
      const existingTables = map.get(row.schema) ?? [];
      map.set(row.schema, [...existingTables, row.table]);
    }
    return Array.from(map.entries()).sort(([left], [right]) =>
      left.localeCompare(right),
    );
  }, [rows]);

  const tablesByKey = useMemo<Record<string, TableMeta>>(() => {
    const cache = values.tablesByKey;
    if (!cache || typeof cache !== "object") return {};
    return cache as Record<string, TableMeta>;
  }, [values.tablesByKey]);

  // On-demand row/column counts for Salesforce and Google Sheets — neither
  // connector front-loads real counts for everything the way HubSpot does
  // (Salesforce: ~1,200 objects is too many; Google Sheets: gridProperties
  // is grid CAPACITY not real data, see google_sheets_kg_mapper.py's
  // compute_real_counts), so counts are fetched live, here, for exactly
  // what's selected. Deliberately mirrors HubSpot's existing Preview
  // behavior on the empty-selection path: Skip shows nothing, no fetch
  // happens.
  const isSalesforce = catalogKey === "salesforce";
  const isGoogleSheets = catalogKey === "google_sheets";
  const supportsLivePreviewCounts = isSalesforce || isGoogleSheets;
  const connectionId =
    typeof values.connectionId === "string" ? values.connectionId : null;
  const objectNames = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((row) => tablesByKey[row.key]?.objectName)
            .filter((name): name is string => Boolean(name)),
        ),
      ),
    [rows, tablesByKey],
  );
  // Google Sheets tab names collide across different files (two selected
  // sheets can both be named "Sheet1"), so the live-count lookup must be
  // keyed by the unambiguous asset id instead of the display name.
  const previewCountAssetIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((row) => tablesByKey[row.key]?.tableAssetId)
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [rows, tablesByKey],
  );
  const previewCountsRequestKey = isGoogleSheets
    ? previewCountAssetIds.join("|")
    : objectNames.join("|");
  const [previewCounts, setPreviewCounts] = useState<
    Record<string, ConnectorPreviewCountsEntryPayload>
  >({});
  const [previewCountsLoading, setPreviewCountsLoading] = useState(false);
  const [previewCountsError, setPreviewCountsError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!supportsLivePreviewCounts || !connectionId || !previewCountsRequestKey) {
      setPreviewCounts({});
      setPreviewCountsError(null);
      return;
    }
    let cancelled = false;
    setPreviewCountsLoading(true);
    setPreviewCountsError(null);
    const request = isGoogleSheets
      ? connectorPreviewCountsApi.fetchByAssetIds(connectionId, previewCountAssetIds)
      : connectorPreviewCountsApi.fetch(connectionId, objectNames);
    void request
      .then((res) => {
        if (cancelled) return;
        setPreviewCounts(res.counts);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPreviewCountsError(
          error instanceof Error
            ? error.message
            : "Failed to load row/column counts.",
        );
      })
      .finally(() => {
        if (!cancelled) setPreviewCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // previewCountsRequestKey is a derived, order-stable string (Set-deduped
    // ids/names joined) — safe as the effective dependency since it only
    // changes when the actual selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportsLivePreviewCounts, isGoogleSheets, connectionId, previewCountsRequestKey]);

  const columns: Array<DataTableColumn<PreviewRow>> = [
    {
      key: "table",
      header: "TABLE",
      bodyVariant: 3,
      emphasis: "high",
      render: (row) => row.table,
    },
    {
      key: "schema",
      header: "SCHEMA",
      render: (row) => row.schema,
    },
    {
      key: "rows",
      header: "ROWS",
      render: (row) => {
        if (supportsLivePreviewCounts) {
          const meta = tablesByKey[row.key];
          const lookupKey = isGoogleSheets ? meta?.tableAssetId : meta?.objectName;
          const count = lookupKey ? previewCounts[lookupKey]?.row_count : null;
          return typeof count === "number" ? `${count.toLocaleString()} rows` : "—";
        }
        const meta = tablesByKey[row.key];
        // rowCountKnown distinguishes "never fetched" from a genuine 0 —
        // meta.rowCount alone can't tell the difference (parseNumber
        // defaults an absent field to 0 too), so checking only `meta ?`
        // rendered "0 rows" for every not-yet-real-counted row (confirmed
        // via live testing 2026-08-28, after gridProperties' placeholder
        // was removed from discovery — this path had been silently masked
        // by that placeholder always being present before).
        return meta?.rowCountKnown ? `${meta.rowCount.toLocaleString()} rows` : "—";
      },
    },
    {
      key: "columns",
      header: "COLUMNS",
      render: (row) => {
        if (supportsLivePreviewCounts) {
          const meta = tablesByKey[row.key];
          const lookupKey = isGoogleSheets ? meta?.tableAssetId : meta?.objectName;
          const count = lookupKey ? previewCounts[lookupKey]?.column_count : null;
          return typeof count === "number" ? `${count} columns` : "—";
        }
        const meta = tablesByKey[row.key];
        return meta?.rowCountKnown ? `${meta.columnCount} columns` : "—";
      },
    },
  ];

  return (
    <Wrap>
      {!(isBiVariant && importMode === "all") ? (
        <Title>{previewHeading}</Title>
      ) : null}
      {supportsLivePreviewCounts && previewCountsError ? (
        <CountsErrorNote>{previewCountsError}</CountsErrorNote>
      ) : null}
      {isBiVariant && importMode === "all" ? (
        <CenteredInfoMessage>{previewAllSelectedMessage}</CenteredInfoMessage>
      ) : rows.length === 0 ? (
        <EmptyMsg>No tables selected. Go back to pick some.</EmptyMsg>
      ) : supportsLivePreviewCounts && previewCountsLoading ? (
        <CenteredInfoMessage role="status">
          <CountsSpinner size={20} />
        </CenteredInfoMessage>
      ) : isBiVariant ? (
        <ListArea>
          <TreePreviewWrap>
          {selectedBiTree.map((node, nodeIndex) => {
            const renderNode = (
              treeNode: BiDatasetTreeNode,
              depth: number,
              isLast: boolean,
            ): React.ReactNode => {
              const isParent = treeNode.children.length > 0;
              const isExpanded = expandedIds.has(treeNode.id);
              return (
                <React.Fragment key={treeNode.id}>
                  <TreeRow $depth={depth} $isLast={isLast}>
                    {isParent ? (
                      <TreeExpander
                        type="button"
                        aria-label={
                          isExpanded
                            ? `Collapse ${treeNode.label}`
                            : `Expand ${treeNode.label}`
                        }
                        onClick={() =>
                          setExpandedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(treeNode.id)) next.delete(treeNode.id);
                            else next.add(treeNode.id);
                            return next;
                          })
                        }
                      >
                        {isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </TreeExpander>
                    ) : (
                      <span />
                    )}
                    {isParent ? (
                      <Folder size={14} color={rawColors.text.mediumEmphasis} />
                    ) : (
                      <FileText size={14} color={rawColors.text.mediumEmphasis} />
                    )}
                    <TreeLabel>{treeNode.label}</TreeLabel>
                  </TreeRow>
                  {isParent && isExpanded
                    ? treeNode.children.map((child, childIndex) =>
                        renderNode(
                          child,
                          depth + 1,
                          childIndex === treeNode.children.length - 1,
                        ),
                      )
                    : null}
                </React.Fragment>
              );
            };
            return renderNode(node, 0, nodeIndex === selectedBiTree.length - 1);
          })}
          </TreePreviewWrap>
        </ListArea>
      ) : (
        <ListArea>
          <ScrollableTableViewport hideScrollbar variant="card">
            <DataTable
              stickyHeader
              columns={columns}
              rows={rows}
              rowKey={(row) => row.key}
            />
          </ScrollableTableViewport>
        </ListArea>
      )}
    </Wrap>
  );
};

export default PreviewStep;
